import { Link } from "@mui/material";

import { faqs, howItWorks, purchaseSeatLanguages } from "./helpers";
import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { vendorUUIDs } from "../../constants";

import FindGamesBanner from "public/images/gwinnettstripers/Get-Hooked.jpg";
import BackgroundImage from "public/images/gwinnettstripers/gwinnettstripersvenue.jpeg";

const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Gwinnett-Stripers-Logo.png";

import type { TeamConfig } from "../types";

const config: TeamConfig = {
  teamSchema: "gwinnettstripers_flex",
  browserTitle: "Stripers Get Hooked Flex Program",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Gwinnett Stripers",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-30720-baseball-sec",
    mapId: "blockmap2d",
    isShowAvailableSeatsButtonHidden: false,
    isView3dButtonHidden: false,
  },

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        minSelectedAmount: 2,
        updatedAccountGroup: 0,
        discount: 10,
        customProgressBarMessage:
          "Congrats - you've unlocked 20% off your tickets! Add 1 more game for exclusive batting practice viewing opportunity from the field, add 2 games for 30% off or add 3+ games for 40% off your tickets!",
      },
      {
        id: 2,
        minSelectedAmount: 3,
        updatedAccountGroup: 0,
        discount: 10,
        customProgressBarMessage:
          "Congrats - you've unlocked an exclusive batting practice viewing opportunity from the field! Add 1 more game for 30% off or add 2+ games for 40% off your tickets!",
        customAditionalMessage:
          " for exclusive batting practice viewing opportunity from the field",
      },
      {
        id: 3,
        minSelectedAmount: 4,
        updatedAccountGroup: 1,
        discount: 30,
        customProgressBarMessage:
          "Congrats - you've unlocked 30% off your tickets! Add 1 more game 40% off your tickets.",
      },
      {
        id: 4,
        minSelectedAmount: 5,
        updatedAccountGroup: 2,
        discount: 40,
        customProgressBarMessage:
          "Congrats - you've unlocked 40% off your tickets! Add more games or checkout now.",
      },
    ],
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 20,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: true,
    defaultSeatCount: 2,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/gwinnettstripers/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: "",
    authBackgroundImage: BackgroundImage.src,
    findGamesBannerImage: FindGamesBanner.src,
    checkoutStatusPageImage: BackgroundImage.src,
    logoImage: LogoImage + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },

    shouldDisplayServiceFee: false,

    progressBar: {
      staticProgressBarText: false,
    },

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact at{" "}
        <Link
          target="_blank"
          href="tel:6782770340"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (678) 277-0340
        </Link>{" "}
        .
      </>
    ),
  },

  colors: {
    primary: "#0d223f",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: purchaseSeatLanguages,
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your Gwinnett Stripers tickets are confirmed! You will be notified when your tickets are available to be managed.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
